import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { toast } from "sonner"
import {
  Activity, CalendarDays, Check, Dumbbell, Gauge, HeartPulse, Scale, Trophy,
} from "lucide-react"
import { useSubscription } from "@/hooks/useSubscription"
import { useAuth } from "@/contexts/AuthContext"
import {
  addWeightEntry,
  getCurrentBodyweightKg,
  isSuiviSeul,
  listActivePrices,
  type StripePriceRow,
  type SubscriptionPlan,
} from "@/lib/supabase"
import { fetchTrialReport, MIN_HOOPER_ENTRIES, type TrialReport } from "@/lib/trialReport"
import { MOVEMENT_LABELS } from "@/lib/strengthStandards"
import { formatFr, formatIntFr, formatDateShortFr } from "@/lib/utils"
import { Card, SectionTitle, SectionCaption, Note } from "@/components/profile/ui"
import { mailtoHref } from "@/data/contact"
import { NumberWheel } from "@/components/shared/NumberWheel"
import { ACCOUNT_PARAM, trialReportGate, weighInOpen } from "@/lib/trialLink"

/**
 * ÉCRAN DE FIN D'ESSAI — bilan des 7 jours, puis les 4 offres (D4, D7).
 *
 * Deux règles tiennent tout l'écran :
 *
 *  1. Le bilan ne dit que du MESURÉ. Pas d'ACWR, pas de Strain, pas d'État de
 *     forme, pas de score de progression, pas de tendance Intensité/Volume : tous
 *     demandent une semaine de référence ou 28 jours d'historique que 7 jours ne
 *     donnent pas. Le détail du raisonnement est dans src/lib/trialReport.ts.
 *
 *     Compte en suivi seul (CONTEXTE §13E) : pas de séance Zeyone, le bilan raconte
 *     ses séances libres — un décompte sans « prévues », jamais « X / Y ». Mêmes
 *     blocs, sauf « Ton travail » (charge soulevée, temps sous tension) qui ne se
 *     mesure que sur les exercices d'une séance Zeyone.
 *
 *  2. Une offre n'est payable que si elle a un prix actif au catalogue
 *     (`stripe_prices.plan`). Autonomie en a un ; Coaching en aura un au lot 6 et
 *     son bouton basculera tout seul. Premium et Lyon n'ont pas de prix fixe :
 *     leur bouton mène à l'appel découverte, jamais au paiement.
 */

// Depuis le lot 4, zeyonefit.com sert la landing (l'app vit sur app.zeyonefit.com) :
// le détail des offres y est, section « offres ».
const LANDING_URL: string | null = "https://zeyonefit.com/#offres"

// Adresse de l'appel découverte : `src/data/contact.ts` (partagée avec le diagnostic).
function discoveryCallHref(offer: string): string {
  return mailtoHref(
    `Appel découverte — ${offer}`,
    `Bonjour Adam,\n\nJe viens de terminer ma semaine d'essai et j'aimerais échanger sur la formule ${offer}.\n\nMes disponibilités : `,
  )
}

interface Offer {
  plan: SubscriptionPlan
  name: string
  pitch: string
  /** Ce que le client obtient, en trois points au plus. */
  points: string[]
  /** Une formule à prix individuel ne peut pas être payée en libre-service. */
  fixedPrice: boolean
  highlight?: boolean
}

const OFFERS: Offer[] = [
  {
    plan: "autonomie",
    name: "Autonomie",
    pitch: "Tu continues seul, avec l'app qui suit tout.",
    points: [
      "Ton programme, séance après séance",
      "Monitoring, progression, nutrition",
      "Résiliable à tout moment",
    ],
    fixedPrice: true,
    highlight: true,
  },
  {
    plan: "coaching",
    name: "Coaching",
    pitch: "L'app, plus moi derrière, à chaque point.",
    points: [
      "Bilan de départ avec moi",
      "Programme validé et réécrit après chaque point",
      "Suivi régulier",
    ],
    fixedPrice: true,
  },
  {
    plan: "premium",
    name: "Premium",
    pitch: "Un accompagnement construit pour toi seul.",
    points: [
      "Bilan de départ et première séance coachée",
      "Programme retouché chaque semaine",
      "Tarif fixé ensemble, selon ton besoin",
    ],
    fixedPrice: false,
  },
  {
    plan: "lyon",
    name: "Lyon — à domicile",
    pitch: "Je viens m'entraîner avec toi, chez toi.",
    points: [
      "Séances à domicile sur Lyon",
      "Service à la personne (crédit d'impôt)",
      "Tarif et rythme fixés ensemble",
    ],
    fixedPrice: false,
  },
]

function formatPrice(price: StripePriceRow): string {
  const amount = (price.amount_cents / 100).toLocaleString("fr-FR", {
    minimumFractionDigits: price.amount_cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })
  const unit = price.interval === "year" ? "an" : price.interval === "month" ? "mois" : null
  const symbol = price.currency?.toUpperCase() === "EUR" ? "€" : price.currency?.toUpperCase() ?? ""
  return unit ? `${amount} ${symbol}/${unit}` : `${amount} ${symbol}`
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`
}

/** Le jour parisien (YYYY-MM-DD) d'un instant — même convention que trialReport.ts. */
function parisDayOf(instant: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date(instant))
}

function formatDayLong(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number)
  const label = new Date(y, m - 1, d).toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long",
  })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

// ── Briques d'affichage ──────────────────────────────────────────────────────

function Stat({ label, value, sub, tone = "#e2e8f0" }: {
  label: string
  value: string
  sub?: string
  tone?: string
}) {
  return (
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-bold uppercase text-gray-500" style={{ letterSpacing: 1.4 }}>{label}</p>
      <p className="text-[22px] font-extrabold leading-tight mt-1 tnum" style={{ color: tone }}>{value}</p>
      {sub && <p className="text-[11px] mt-0.5" style={{ color: "#64748b" }}>{sub}</p>}
    </div>
  )
}

function Block({ icon: Icon, title, children }: {
  icon: React.FC<{ className?: string }>
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="px-4 mt-6">
      <SectionTitle
        label={title}
        right={<Icon className="w-3.5 h-3.5 text-gray-600" />}
      />
      <Card>
        <div className="p-4">{children}</div>
      </Card>
    </div>
  )
}

// ── Pesée du jour ────────────────────────────────────────────────────────────

// Bornes inchangées (plus de 20 kg, au plus 400 kg), pas de 0,5 kg comme la pesée du jour.
const WEIGH_IN = { min: 20.5, max: 400, step: 0.5, fallback: 75 }

function WeighIn({ reportDay, onSaved }: { reportDay: string; onSaved: () => void }) {
  const { user } = useAuth()
  // null tant que la dernière pesée n'est pas lue : la molette ne lit sa position
  // de départ qu'au montage, elle n'apparaît donc qu'une fois cette valeur connue.
  const [value, setValue] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!user?.id) return
    let mounted = true
    // Dernière pesée, repli sur le poids du diagnostic ; gardée telle quelle (72,4).
    getCurrentBodyweightKg(user.id)
      .catch(() => null)
      .then((kg) => { if (mounted) setValue(kg != null && kg > 0 ? kg : WEIGH_IN.fallback) })
    return () => { mounted = false }
  }, [user?.id])

  const submit = async () => {
    if (value === null) return
    setSaving(true)
    try {
      await addWeightEntry(reportDay, value)
      onSaved()
    } catch (err) {
      console.error("[TrialReportPage] pesée :", err)
      toast.error("Enregistrement impossible", { description: "Réessaie dans un instant." })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <p className="text-[13px] leading-relaxed" style={{ color: "#94a3b8" }}>
        Pèse-toi ce matin, à jeun, pour compléter ton bilan.
      </p>
      {value === null ? (
        <div className="h-[146px] mt-3 flex items-center justify-center">
          <div className="w-6 h-6 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
        </div>
      ) : (
        <div className="flex justify-center mt-3">
          <NumberWheel
            id="trial-weigh-in"
            label="Poids du jour"
            labelHidden
            unit="kg"
            min={WEIGH_IN.min}
            max={WEIGH_IN.max}
            step={WEIGH_IN.step}
            value={value}
            onChange={setValue}
          />
        </div>
      )}
      <button
        onClick={submit}
        disabled={saving || value === null}
        className="w-full mt-3 px-5 py-3 rounded-xl text-[14px] font-bold cursor-pointer disabled:opacity-50"
        style={{ background: "#06b6d4", color: "#04222b" }}
      >
        {saving ? "…" : "Valider"}
      </button>
    </div>
  )
}

// ── Écran ────────────────────────────────────────────────────────────────────

export function TrialReportPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut, wizardData } = useAuth()
  // Pour les écrans d'attente, avant que le bilan (qui porte son propre `mode`) soit lu.
  const suiviSeulAccount = isSuiviSeul(wizardData)
  const { access, loading: subLoading, startCheckout, isActive } = useSubscription()
  const [report, setReport] = useState<TrialReport | null>(null)
  const [reportLoading, setReportLoading] = useState(true)
  const [prices, setPrices] = useState<StripePriceRow[]>([])
  const [checkoutPlan, setCheckoutPlan] = useState<SubscriptionPlan | null>(null)

  const startedAt = access?.trial_started_at ?? null
  const endsAt = access?.trial_ends_at ?? null

  // Ce que l'écran doit montrer (src/lib/trialLink.ts) : le lien de l'email peut être
  // ouvert depuis un autre compte, pendant l'essai, ou après un abonnement.
  // null tant que le droit d'accès n'est pas lu.
  const gate = subLoading || !user
    ? null
    : trialReportGate({
        linkTag: new URLSearchParams(location.search).get(ACCOUNT_PARAM),
        userId: user.id,
        access,
      })

  const load = useCallback(async () => {
    // Le bilan ne se calcule que s'il va être montré. Avant, un compte sans essai
    // (abonné, accès manuel) restait sur le chargement : `reportLoading` ne
    // retombait jamais, et l'écran « pas de semaine d'essai » n'apparaissait pas.
    // Droit pas encore lu (gate null) : on ne touche à rien, sinon l'écran « pas de
    // semaine d'essai » s'afficherait un instant avant le bilan.
    if (gate === null) return
    if (gate !== "report" || !user?.id || !startedAt || !endsAt) {
      setReportLoading(false)
      return
    }
    setReportLoading(true)
    try {
      setReport(await fetchTrialReport(user.id, startedAt, endsAt))
    } catch (err) {
      console.error("[TrialReportPage] bilan :", err)
    } finally {
      setReportLoading(false)
    }
  }, [gate, user?.id, startedAt, endsAt])

  useEffect(() => { load() }, [load])
  useEffect(() => { listActivePrices().then(setPrices) }, [])

  const priceByPlan = useMemo(() => {
    const map = new Map<SubscriptionPlan, StripePriceRow>()
    for (const p of prices) {
      if (p.plan && !map.has(p.plan)) map.set(p.plan, p)
    }
    return map
  }, [prices])

  const handleCheckout = async (plan: SubscriptionPlan) => {
    setCheckoutPlan(plan)
    try {
      await startCheckout(plan)
    } catch (err) {
      console.error("[TrialReportPage] checkout :", err)
      toast.error("Paiement indisponible", { description: "Réessaie dans un instant." })
      setCheckoutPlan(null)
    }
  }

  if (gate === null || (gate === "report" && reportLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#060a13" }}>
        <div className="w-8 h-8 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
      </div>
    )
  }

  // L'essai court encore : il n'y a pas de bilan de 7 jours à montrer, et l'écran
  // ferait trois choses fausses — titrer « 7 jours, et voilà ce que ça donne » sur
  // une semaine inachevée, proposer une pesée datée de J8 (donc dans le futur), et
  // afficher les offres avec un bouton de paiement désactivé.
  //
  // La garde est ICI et pas seulement dans le lien de la notification : l'écran
  // reste atteignable par son URL.
  if (gate === "other-account") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ background: "#060a13" }}>
        <p className="text-[10px] font-bold uppercase" style={{ color: "#06b6d4", letterSpacing: 2 }}>
          Bilan de fin d'essai
        </p>
        <h1 className="text-[20px] font-bold text-white leading-snug">Ce bilan est pour un autre compte</h1>
        <p className="text-[14px] leading-relaxed max-w-xs" style={{ color: "#94a3b8" }}>
          Tu es connecté ici avec {user?.email ?? "un autre compte"}. Pour voir ce bilan,
          déconnecte-toi, puis reconnecte-toi avec l'adresse qui a reçu l'email.
        </p>
        {/* Déconnexion de CET appareil seulement : le compte connecté n'est pas
            celui du client qui vient voir son bilan, il ne doit pas perdre ses
            sessions ailleurs. La route renvoie ensuite à la connexion, retour ici. */}
        <button
          onClick={() => { void signOut("local") }}
          className="mt-2 px-6 py-3 rounded-xl text-[14px] font-bold cursor-pointer"
          style={{ background: "#06b6d4", color: "#04222b" }}
        >
          Me déconnecter
        </button>
        <button
          onClick={() => navigate("/dashboard")}
          className="px-6 py-2 text-[13px] font-semibold cursor-pointer"
          style={{ color: "#94a3b8" }}
        >
          Aller à mon accueil
        </button>
      </div>
    )
  }

  if (gate === "subscribed") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ background: "#060a13" }}>
        <p className="text-[10px] font-bold uppercase" style={{ color: "#06b6d4", letterSpacing: 2 }}>
          Ton abonnement
        </p>
        <h1 className="text-[20px] font-bold text-white leading-snug">Ton abonnement est actif</h1>
        <p className="text-[14px] leading-relaxed max-w-xs" style={{ color: "#94a3b8" }}>
          Ta semaine d'essai est derrière toi : {suiviSeulAccount ? "ton suivi" : "ton programme"} continue depuis ton accueil.
        </p>
        <button
          onClick={() => navigate("/dashboard")}
          className="mt-2 px-6 py-3 rounded-xl text-[14px] font-bold cursor-pointer"
          style={{ background: "#06b6d4", color: "#04222b" }}
        >
          Aller à mon accueil
        </button>
      </div>
    )
  }

  if (gate === "in-trial" && endsAt) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ background: "#060a13" }}>
        <p className="text-[10px] font-bold uppercase" style={{ color: "#06b6d4", letterSpacing: 2 }}>
          Semaine d'essai en cours
        </p>
        <h1 className="text-[20px] font-bold text-white leading-snug">Ton bilan arrive</h1>
        <p className="text-[14px] leading-relaxed max-w-xs" style={{ color: "#94a3b8" }}>
          Il sera prêt le {formatDayLong(parisDayOf(endsAt))} au matin, avec tes 7 jours complets.
          {suiviSeulAccount ? "D'ici là, continue d'ajouter tes séances." : "D'ici là, tu as encore des séances à faire."}
        </p>
        <button
          onClick={() => navigate("/dashboard")}
          className="mt-2 px-6 py-3 rounded-xl text-[14px] font-bold cursor-pointer"
          style={{ background: "#06b6d4", color: "#04222b" }}
        >
          Retour à l'accueil
        </button>
      </div>
    )
  }

  // Aucun essai à raconter (compte payant, accès manuel) : l'écran n'a pas lieu d'être.
  if (gate !== "report" || !startedAt || !endsAt || !report) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ background: "#060a13" }}>
        <p className="text-[15px]" style={{ color: "#94a3b8" }}>Pas de semaine d'essai à afficher pour ce compte.</p>
        <button
          onClick={() => navigate("/dashboard")}
          className="px-6 py-3 rounded-xl text-[14px] font-bold cursor-pointer"
          style={{ background: "#06b6d4", color: "#04222b" }}
        >
          Retour à l'accueil
        </button>
      </div>
    )
  }

  const { hooper, weight, strength } = report
  const suiviSeul = report.mode === "suivi_seul"
  // Suivi seul sans aucune séance : pas de « 0 » sec, une invitation à la place.
  const noFreeSession = suiviSeul && report.sessions.length === 0

  return (
    <div className="min-h-screen pb-16" style={{ background: "#060a13" }}>
      {/* En-tête */}
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="px-5 pt-12 pb-2">
        <p className="text-[10px] font-bold uppercase" style={{ color: "#06b6d4", letterSpacing: 2 }}>
          Ta semaine d'essai
        </p>
        <h1 className="text-[26px] font-extrabold text-white tracking-tight mt-1.5">
          7 jours, et voilà ce que ça donne
        </h1>
        <p className="text-[13px] mt-1.5" style={{ color: "#64748b" }}>
          Du {formatDateShortFr(report.window.workFromDay)} au {formatDateShortFr(report.window.toDay)}.
          Rien d'estimé ici : uniquement ce que tu as fait.
        </p>
      </motion.div>

      {/* Ce que tu as fait */}
      <Block icon={CalendarDays} title="Ce que tu as fait">
        {noFreeSession ? (
          <p className="text-[13px] leading-relaxed" style={{ color: "#94a3b8" }}>
            Tu n'as pas encore ajouté de séance. Ajoute la première — avec ton coach ou
            seul — et l'app commence à suivre ta charge et ton ressenti, séance après séance.
          </p>
        ) : (
        <div className="flex gap-4">
          {suiviSeul ? (
            <Stat
              label="Séances réalisées"
              value={formatIntFr(report.sessionsDone)}
              sub={report.sessionsWithoutQuestionnaire > 0
                ? `Dont ${report.sessionsWithoutQuestionnaire} sans questionnaire`
                : undefined}
              tone="#06b6d4"
            />
          ) : (
          <Stat
            label="Séances"
            value={`${report.sessionsDone} / ${report.sessionsPlanned}`}
            sub={report.sessionsDone < report.sessionsPlanned
              ? "Les séances non faites restent dans ton programme"
              : "Semaine complète"}
            tone="#06b6d4"
          />
          )}
          <Stat
            label="Temps d'entraînement"
            value={report.totalDurationMinutes > 0 ? formatDuration(report.totalDurationMinutes) : "—"}
            sub={suiviSeul && report.sessionsWithoutQuestionnaire > 0 ? "Séances avec questionnaire" : undefined}
          />
        </div>
        )}
        {report.sessions.length > 0 && (
          <div className="mt-4 pt-4 flex flex-col gap-2" style={{ borderTop: "1px solid rgba(30,41,59,0.55)" }}>
            {report.sessions.map((s) => (
              <div key={`${s.date}-${s.title}`} className="flex items-center gap-2.5">
                <span
                  className="w-[22px] h-[22px] rounded-full flex items-center justify-center shrink-0"
                  style={{
                    background: s.done ? "rgba(34,197,94,0.14)" : "rgba(148,163,184,0.10)",
                    border: `1px solid ${s.done ? "rgba(34,197,94,0.30)" : "rgba(148,163,184,0.20)"}`,
                  }}
                >
                  {s.done && <Check className="w-3 h-3" style={{ color: "#22c55e" }} />}
                </span>
                <span className="text-[13px] flex-1 min-w-0 truncate" style={{ color: s.done ? "#e2e8f0" : "#64748b" }}>
                  {s.title}
                </span>
                <span className="text-[11px] shrink-0" style={{ color: "#64748b" }}>
                  {formatDayLong(s.date).split(" ").slice(0, 3).join(" ")}
                </span>
              </div>
            ))}
          </div>
        )}
      </Block>

      {/* Ton travail — mesuré sur les exercices des séances Zeyone, sans objet en suivi seul */}
      {!suiviSeul && (
      <Block icon={Dumbbell} title="Ton travail">
        <div className="flex gap-4">
          <Stat
            label="Charge soulevée"
            value={report.intensityKg > 0 ? `${formatIntFr(report.intensityKg)} kg` : "—"}
            sub="Somme de tout ce que tu as déplacé"
          />
          <Stat
            label="Temps sous tension"
            value={report.volumeSeconds > 0 ? formatDuration(report.volumeSeconds / 60) : "—"}
            sub="Muscle réellement sous charge"
          />
        </div>
        {(report.intensityCoveragePct < 100 || report.volumeCoveragePct < 100) && (
          <p className="text-[11px] mt-3 leading-relaxed" style={{ color: "#64748b" }}>
            Certaines séries ne se comptent pas exactement (drop sets, séries à fourchette) :
            {" "}{report.intensityCoveragePct} % des séries entrent dans la charge,
            {" "}{report.volumeCoveragePct} % dans le temps sous tension. Les totaux sont donc
            un plancher, jamais un maximum.
          </p>
        )}
      </Block>
      )}

      {/* Ton ressenti d'effort — rien à en dire sans séance (suivi seul) */}
      {!noFreeSession && (
      <Block icon={Gauge} title="Ton ressenti d'effort">
        <div className="flex gap-4">
          <Stat
            label="RPE moyen"
            value={report.averageRpe !== null ? `${formatFr(report.averageRpe, 1)} / 10` : "—"}
            sub="Ce que tu as déclaré après chaque séance"
          />
          <Stat
            label="Charge totale"
            value={report.totalLoadAu > 0 ? `${formatIntFr(report.totalLoadAu)} UA` : "—"}
            sub={suiviSeul ? "RPE × durée, séances avec questionnaire" : "RPE × durée, méthode sRPE"}
          />
        </div>
        <SectionCaption>
          Pas de zone de couleur ici : la charge d'une semaine est une vraie mesure, mais la
          lire (surcharge, sous-entraînement) demande quatre semaines d'historique.
        </SectionCaption>
      </Block>
      )}

      {/* Ta récupération */}
      <Block icon={HeartPulse} title="Ta récupération">
        {hooper.averageScore === null ? (
          <p className="text-[13px] leading-relaxed" style={{ color: "#94a3b8" }}>
            {hooper.entries === 0
              ? "Tu n'as rempli aucun questionnaire du matin cette semaine."
              : `${hooper.entries} questionnaire${hooper.entries > 1 ? "s" : ""} du matin rempli${hooper.entries > 1 ? "s" : ""} : pas assez pour une moyenne qui veuille dire quelque chose (il en faut ${MIN_HOOPER_ENTRIES}).`}
          </p>
        ) : (
          <>
            <div className="flex gap-4">
              <Stat
                label="Score Hooper moyen"
                value={`${formatFr(hooper.averageScore, 1)} / 28`}
                sub={suiviSeul
                  ? `${hooper.entries} jour${hooper.entries > 1 ? "s" : ""} rempli${hooper.entries > 1 ? "s" : ""}`
                  : `${hooper.entries} questionnaire${hooper.entries > 1 ? "s" : ""} sur 7`}
                tone="#06b6d4"
              />
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-4 pt-4" style={{ borderTop: "1px solid rgba(30,41,59,0.55)" }}>
              {([
                ["Sommeil", hooper.averageSleep],
                ["Fraîcheur", hooper.averageFreshness],
                ["Humeur", hooper.averageMood],
                ["Courbatures", hooper.averageSoreness],
              ] as const).map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between">
                  <span className="text-[12px]" style={{ color: "#94a3b8" }}>{label}</span>
                  <span className="text-[13px] font-semibold text-white tnum">
                    {value !== null ? `${formatFr(value, 1)} / 7` : "—"}
                  </span>
                </div>
              ))}
            </div>
            {hooper.bestDay && hooper.worstDay && (
              <p className="text-[11px] mt-3" style={{ color: "#64748b" }}>
                Meilleur jour : {formatDateShortFr(hooper.bestDay.date)} ({hooper.bestDay.score}/28) ·
                {" "}Moins bon : {formatDateShortFr(hooper.worstDay.date)} ({hooper.worstDay.score}/28).
                Plus le score est bas, mieux tu récupères.
              </p>
            )}
          </>
        )}
      </Block>

      {/* Ton poids */}
      <Block icon={Scale} title="Ton poids">
        {weight.reportKg === null ? (
          weighInOpen(report.window.reportDay, parisDayOf(new Date().toISOString())) ? (
            <WeighIn reportDay={report.window.reportDay} onSaved={load} />
          ) : (
            // Bilan rouvert après le matin de J8 : une pesée d'aujourd'hui serait
            // enregistrée à la date de J8 et fausserait l'écart (trialLink.ts).
            <p className="text-[13px] leading-relaxed" style={{ color: "#94a3b8" }}>
              Pas de pesée le matin de ta fin d'essai ({formatDayLong(report.window.reportDay)}).
              Une pesée faite plus tard ne dirait rien de ta semaine : elle n'entre pas
              dans ce bilan.
            </p>
          )
        ) : (
          <>
            <div className="flex gap-4">
              <Stat label="Au diagnostic" value={weight.startKg !== null ? `${formatFr(weight.startKg, 1)} kg` : "—"} tone="#94a3b8" />
              <Stat label="Fin d'essai" value={`${formatFr(weight.reportKg, 1)} kg`} tone="#94a3b8" />
              {weight.deltaKg !== null && (
                <Stat
                  label="Écart"
                  value={`${weight.deltaKg > 0 ? "+" : ""}${formatFr(weight.deltaKg, 1)} kg`}
                  tone="#94a3b8"
                />
              )}
            </div>
            {/* Volontairement en gris, sans couleur d'objectif : sur 7 jours, l'écart
                est surtout de l'eau et de la digestion. La règle GOAL_DIRECTION de la
                page Progrès ne s'applique pas ici. */}
            <SectionCaption>
              Une semaine ne suffit pas à dégager une tendance : cet écart est surtout de
              l'eau et de la digestion. C'est à partir de trois ou quatre pesées
              hebdomadaires qu'une direction se lit.
            </SectionCaption>
          </>
        )}
      </Block>

      {/* Ta force */}
      {strength.length > 0 && (
        <Block icon={Trophy} title="Ta force">
          <div className="flex flex-col gap-2.5">
            {strength.map((s) => (
              <div key={s.movement} className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] min-w-0 truncate" style={{ color: "#e2e8f0" }}>
                  {MOVEMENT_LABELS[s.movement]}
                </span>
                <span className="text-[13px] font-semibold text-white tnum shrink-0">
                  {formatFr(s.measuredKg, s.measuredKg % 1 === 0 ? 0 : 1)} kg
                  {s.targetKg !== null && (
                    <span className="font-normal" style={{ color: "#64748b" }}>
                      {" "}· cible {formatFr(s.targetKg, s.targetKg % 1 === 0 ? 0 : 1)} kg
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
          <SectionCaption>
            Mesuré pendant ton test de force, pas estimé. La cible est celle de ton bloc en cours.
          </SectionCaption>
        </Block>
      )}

      {/* Ce qui vient ensuite */}
      <Block icon={Activity} title="Ce qui vient ensuite">
        <p className="text-[13px] leading-relaxed" style={{ color: "#94a3b8" }}>
          {suiviSeul
            ? "Continue d'ajouter tes séances, avec ton coach ou seul : chacune compte dans ta charge et ton suivi."
            : <>Ta semaine 2 est écrite : {report.sessionsDone < report.sessionsPlanned
                ? "tes séances non faites t'attendent, suivies des suivantes."
                : "quatre nouvelles séances, dans la continuité de celles-ci."}</>}
        </p>
        <p className="text-[13px] leading-relaxed mt-2" style={{ color: "#94a3b8" }}>
          À partir de maintenant, l'app commence à mesurer ce qu'une seule semaine ne
          permettait pas : ta charge sur quatre semaines, ta fatigue cumulée, et ta
          progression d'une semaine à l'autre.
        </p>
      </Block>

      {/* ── Les 4 offres ── */}
      <div className="px-4 mt-8">
        <SectionTitle label="La suite, c'est toi qui choisis" />
        <div className="flex flex-col gap-3">
          {OFFERS.map((offer) => {
            const price = offer.fixedPrice ? priceByPlan.get(offer.plan) ?? null : null
            const payable = price !== null
            const busy = checkoutPlan === offer.plan
            return (
              <Card
                key={offer.plan}
                style={offer.highlight ? { border: "1px solid rgba(6,182,212,0.35)" } : undefined}
              >
                <div className="p-4">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-[16px] font-bold text-white">{offer.name}</p>
                    <p className="text-[14px] font-bold shrink-0 tnum" style={{ color: payable ? "#06b6d4" : "#94a3b8" }}>
                      {price ? formatPrice(price) : "Sur mesure"}
                    </p>
                  </div>
                  <p className="text-[12.5px] mt-1" style={{ color: "#94a3b8" }}>{offer.pitch}</p>
                  <ul className="mt-3 flex flex-col gap-1.5">
                    {offer.points.map((point) => (
                      <li key={point} className="flex items-start gap-2 text-[12.5px]" style={{ color: "#cbd5e1" }}>
                        <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#06b6d4" }} />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>

                  {payable ? (
                    <button
                      onClick={() => handleCheckout(offer.plan)}
                      disabled={busy || isActive}
                      className="w-full mt-4 py-3 rounded-xl text-[14px] font-bold cursor-pointer disabled:opacity-60"
                      style={{ background: "linear-gradient(135deg, #0369a1, #0891b2, #06b6d4)", color: "#fff" }}
                    >
                      {busy ? "Redirection…" : `Prendre ${offer.name}`}
                    </button>
                  ) : (
                    <a
                      href={discoveryCallHref(offer.name)}
                      className="block w-full mt-4 py-3 rounded-xl text-[14px] font-bold text-center cursor-pointer"
                      style={{
                        background: "rgba(6,182,212,0.08)",
                        border: "1px solid rgba(6,182,212,0.28)",
                        color: "#06b6d4",
                      }}
                    >
                      Demander un appel découverte
                    </a>
                  )}
                  {!offer.fixedPrice && (
                    <p className="text-[11px] mt-2 text-center" style={{ color: "#64748b" }}>
                      Pas de prix fixe : on le définit ensemble, selon ton besoin.
                    </p>
                  )}
                </div>
              </Card>
            )
          })}
        </div>

        {LANDING_URL !== null && (
          <a
            href={LANDING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center text-[12px] mt-4 underline"
            style={{ color: "#64748b" }}
          >
            Voir le détail des offres
          </a>
        )}

        <Note>
          Tes données sont conservées : si tu t'abonnes plus tard, tu retrouves ton
          programme, tes séances et tes records là où tu les as laissés.
        </Note>

        <button
          onClick={() => navigate("/dashboard")}
          className="w-full mt-4 py-3 rounded-xl text-[13.5px] font-semibold cursor-pointer"
          style={{ border: "1px solid rgba(255,255,255,0.13)", background: "rgba(255,255,255,0.05)", color: "#cbd5e1" }}
        >
          Plus tard
        </button>
      </div>
    </div>
  )
}
